import { useEffect, useMemo, useState } from 'react'
import ReactECharts from 'echarts-for-react'
import { Layers3 } from 'lucide-react'
import type { WireArticle } from '../../api/types'

interface ContentMixWidgetProps {
  articles: WireArticle[]
  embedded?: boolean
}

type ChartColors = {
  fg: string
  line: string
  tooltipBg: string
  tooltipFg: string
}

function resolveChartColors(): ChartColors {
  if (typeof window === 'undefined') {
    return {
      fg: '#111111',
      line: '#e5e7eb',
      tooltipBg: '#111111',
      tooltipFg: '#fcfcfc',
    }
  }
  const s = getComputedStyle(document.documentElement)
  const get = (v: string) => s.getPropertyValue(v).trim()
  return {
    fg: get('--gray-12') || '#111111',
    line: get('--gray-6') || '#e5e7eb',
    tooltipBg: get('--gray-12') || '#111111',
    tooltipFg: get('--gray-1') || '#fcfcfc',
  }
}

function useChartTheme(): ChartColors {
  const [colors, setColors] = useState<ChartColors>(resolveChartColors)
  useEffect(() => {
    const observer = new MutationObserver(() => setColors(resolveChartColors()))
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] })
    return () => observer.disconnect()
  }, [])
  return colors
}

function normalizeProductType(value: string | null | undefined): string {
  if (!value) return 'Unknown'
  return value
    .replace(/[_-]/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase())
}

export function ContentMixWidget({ articles, embedded = false }: ContentMixWidgetProps) {
  const colors = useChartTheme()

  const productMix = useMemo(() => {
    const counts = new Map<string, number>()
    for (const article of articles) {
      const key = normalizeProductType(article.productType)
      counts.set(key, (counts.get(key) ?? 0) + 1)
    }
    return Array.from(counts.entries())
      .map(([name, value]) => ({ name, value }))
      .sort((a, b) => b.value - a.value)
  }, [articles])

  const total = useMemo(() => productMix.reduce((sum, item) => sum + item.value, 0), [productMix])
  const top = productMix[0]

  const option = useMemo(() => {
    if (productMix.length === 0) return null
    return {
      tooltip: {
        trigger: 'item' as const,
        backgroundColor: colors.tooltipBg,
        borderColor: colors.line,
        textStyle: { color: colors.tooltipFg, fontSize: 11 },
        formatter: (params: { name: string; value: number; percent: number }) =>
          `${params.name}: ${params.value} draft${params.value !== 1 ? 's' : ''} (${params.percent}%)`,
      },
      legend: {
        bottom: 4,
        textStyle: { color: colors.fg, fontSize: 11, fontWeight: 600 },
      },
      series: [
        {
          name: 'Content mix',
          type: 'pie' as const,
          radius: ['38%', '68%'],
          center: ['50%', '42%'],
          label: {
            formatter: '{b}: {c}',
            color: colors.fg,
            fontSize: 11,
            fontWeight: 600,
          },
          data: productMix,
        },
      ],
    }
  }, [productMix, colors])

  const body = (
    <div className="space-y-2 px-4 py-4">
      {option ? (
        <div className="rounded-xl bg-canvas px-2 py-2">
          <ReactECharts option={option} style={{ height: 250 }} />
        </div>
      ) : (
        <div className="rounded-xl bg-canvas px-4 py-6 text-center text-[11px] text-fg-secondary">
          No drafts available for content mix.
        </div>
      )}

      <div className="rounded-xl bg-fill/60 px-4 py-3 text-[11px] text-fg-secondary">
        {total > 0 && top
          ? `${total} draft${total !== 1 ? 's' : ''} tracked. Largest segment: ${top.name} (${top.value}).`
          : 'No in-progress draft mix to summarize yet.'}
      </div>
    </div>
  )

  if (embedded) return <div>{body}</div>

  return (
    <section className="flex flex-col rounded border border-line bg-surface">
      <div className="flex items-center gap-2 border-b border-line px-5 py-3">
        <Layers3 size={14} className="text-fg-secondary" />
        <span className="font-sans text-[10px] font-bold uppercase tracking-widest text-fg-secondary">Content Mix</span>
      </div>
      {body}
    </section>
  )
}
